// frontend/src/components/common/ColumnsMenu.tsx
import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'

export interface ColumnItem {
  key: string
  label: string
}

interface Props {
  dark: boolean
  /** Columnas conmutables, en el orden en que se muestran. */
  items: ColumnItem[]
  /** Conjunto de keys actualmente visibles. */
  visible: Set<string>
  onToggle: (key: string) => void
  btnLabel: string
  title: string
}

const MENU_W = 220

/** Aire entre el final del panel y el borde inferior de la ventana. */
const MARGEN_INFERIOR = 16

/**
 * Botón + popover para mostrar/ocultar columnas de una tabla.
 * Reusa el patrón portal + overlay de ColumnFilterPopover para escapar
 * del overflow/stacking de la tabla.
 */
export function ColumnsMenu({ dark, items, visible, onToggle, btnLabel, title }: Props) {
  const btnRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const open = rect !== null

  function toggleOpen() {
    setRect(open ? null : (btnRef.current?.getBoundingClientRect() ?? null))
  }

  // Cierra al hacer scroll (la posición fija quedaría desfasada) o al redimensionar.
  //
  // El listener va en fase de captura para enterarse del scroll de cualquier
  // contenedor, no sólo el de la ventana. Eso incluye el de la PROPIA lista, que
  // ahora se desplaza: sin la excepción, el menú se cerraba en cuanto el usuario
  // intentaba bajar por él.
  useEffect(() => {
    if (!open) return
    const close = (e: Event) => {
      if (e.target instanceof Node && panelRef.current?.contains(e.target)) return
      setRect(null)
    }
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  const hidden = items.length - items.filter(i => visible.has(i.key)).length
  const btnBase = 'text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors font-medium cursor-pointer flex items-center gap-1'
  const btnCls = dark
    ? `${btnBase} border-white/10 bg-white/5 text-slate-300 hover:bg-white/10`
    : `${btnBase} border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm`
  const card = dark ? 'bg-slate-900 border-white/10 text-slate-200' : 'bg-white border-slate-200 text-slate-700'
  const rowCls = `flex items-center gap-2 text-[12px] cursor-pointer px-2 py-1.5 rounded-lg ${
    dark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
  }`

  const left = rect ? Math.max(8, Math.min(rect.left, window.innerWidth - MENU_W - 8)) : 0
  const top = rect ? rect.bottom + 4 : 0

  /**
   * Alto máximo de la lista: lo que queda hasta el borde inferior de la ventana.
   *
   * Sin esto el panel crecía con el número de columnas y se salía de la pantalla
   * por abajo, sin forma de llegar a las últimas: los reports de cliente llegan a
   * 42 columnas, que son más de mil píxeles de lista. El tope de 160 evita el
   * caso degenerado de abrirlo con el botón casi pegado al borde inferior.
   */
  const altoLista = rect ? Math.max(160, window.innerHeight - top - MARGEN_INFERIOR) : 0

  return (
    <>
      <button ref={btnRef} onClick={toggleOpen} className={btnCls} title={title} aria-expanded={open}>
        <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="2.5" width="12" height="11" rx="1.5" />
          <path d="M6.5 2.5v11M10 2.5v11" />
        </svg>
        {btnLabel}
        {hidden > 0 && <span className="text-sky-500 font-bold">{hidden}</span>}
        <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2.5 4.5L6 8l3.5-3.5" />
        </svg>
      </button>

      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setRect(null)} aria-hidden="true" />
          <div
            ref={panelRef}
            className={`fixed z-[61] rounded-xl border shadow-2xl p-2 flex flex-col ${card}`}
            style={{ top, left, width: MENU_W, maxHeight: altoLista }}
            onClick={e => e.stopPropagation()}
          >
            {/* El rótulo se queda fijo y sólo se desplaza la lista: con 42 filas,
                perder de vista de qué es el menú al bajar no ayuda. */}
            <div className={`shrink-0 px-2 pt-1 pb-2 text-[10px] font-bold uppercase tracking-wider ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
              {title}
            </div>
            <div className="flex flex-col gap-0.5 overflow-y-auto overscroll-contain min-h-0">
              {items.map(it => {
                const on = visible.has(it.key)
                return (
                  <label key={it.key} className={rowCls}>
                    <input type="checkbox" checked={on} onChange={() => onToggle(it.key)} className="cursor-pointer accent-sky-500" />
                    <span className={on ? '' : (dark ? 'text-slate-500' : 'text-slate-400')}>{it.label}</span>
                  </label>
                )
              })}
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  )
}
